"use strict";
/**
 * RECOVERY INDEX — la media ponderata che produce il RI (0-100).
 *
 * Vive qui, e non più solo in `apps/backend`, per una ragione pratica: la
 * dashboard interna deve poter RIESEGUIRE la formula sugli input registrati nel
 * lineage e confrontare il risultato con quello salvato. Finché
 * l'implementazione stava dentro il backend principale, l'unico modo per
 * verificarla dall'esterno sarebbe stato riscriverla — cioè creare una seconda
 * fonte di verità destinata a divergere in silenzio, che è esattamente ciò che
 * il calc engine esiste per impedire.
 *
 * Sono funzioni pure: nessun accesso al DB, nessuna data, nessun IO. Chi legge
 * i fattori dal database resta in `recovery.service.ts`.
 *
 * Spec: LEOPART_Readiness_Spec_v1, sez. 3.2/3.3.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MUSCULAR_WEIGHTS = void 0;
exports.rebalanceWeights = rebalanceWeights;
exports.calculateRI = calculateRI;
exports.muscularRecovery = muscularRecovery;
/**
 * Ridistribuzione proporzionale dei pesi sui soli fattori presenti.
 *
 * Un atleta che non ha fatto il CMJ non deve essere penalizzato: il peso del
 * fattore mancante si spalma sugli altri, così la somma resta 1.0 e il RI
 * continua a essere confrontabile fra atleti con dati diversi.
 *
 * Input: pesi base (es. `{cmj:25, fcr:22.5, hrv:25, hooper:27.5}`) e l'insieme
 * dei fattori disponibili. Output: pesi normalizzati (somma 1.0), 0 per gli
 * assenti. Se non c'è nessun fattore, tutti a 0 — il chiamante decide cosa
 * farne.
 */
function rebalanceWeights(baseWeights, available) {
    const totalAvailable = Object.entries(baseWeights)
        .filter(([k]) => available.has(k))
        .reduce((sum, [, w]) => sum + w, 0);
    if (totalAvailable === 0) {
        const result = {};
        for (const k of Object.keys(baseWeights)) {
            result[k] = 0;
        }
        return result;
    }
    const result = {};
    for (const [k, w] of Object.entries(baseWeights)) {
        result[k] = available.has(k) ? w / totalAvailable : 0;
    }
    return result;
}
/**
 * RI finale (0-100) dalle interpolazioni dei fattori e dai pesi base.
 *
 * I fattori a `null`/`undefined` sono "non misurati" e vengono esclusi dal
 * calcolo (non valgono zero: un dato assente non è un recupero pessimo).
 * Nessun fattore disponibile → 0.
 *
 * Il risultato è arrotondato a un decimale: è la precisione con cui il RI viene
 * persistito e mostrato, e arrotondare qui evita che confronti e ricalcoli
 * inseguano cifre che nessuno usa.
 */
function calculateRI(factors, baseWeights) {
    var _a, _b;
    const available = new Set(Object.keys(factors).filter((k) => factors[k] != null));
    if (available.size === 0)
        return 0;
    const adjusted = rebalanceWeights(baseWeights, available);
    let ri = 0;
    for (const key of available) {
        ri += ((_a = factors[key]) !== null && _a !== void 0 ? _a : 0) * ((_b = adjusted[key]) !== null && _b !== void 0 ? _b : 0);
    }
    return Math.round(ri * 10) / 10;
}
// ─── Fattore Muscolare del RI (CMJ + DOMS) ──────────────────────────────────
/**
 * Pesi base del fattore Muscolare: oggettivo (CMJ/RSI) + soggettivo (DOMS).
 * Ripristinati dallo spec Readiness v1 (era `MUSCOLARE_WEIGHTS rsi .65/doms .35`,
 * poi persi nel passaggio a v2 dove il Muscolare era rimasto solo CMJ).
 */
exports.MUSCULAR_WEIGHTS = { cmj: 0.65, doms: 0.35 };
/**
 * Fattore Muscolare del RI (0-100) come media pesata di CMJ e DOMS, entrambi
 * già in scala recovery (0-100). Il peso .35 del DOMS è DINAMICO: se uno dei
 * due manca, il suo peso si spalma sull'altro via `rebalanceWeights` (un CMJ
 * assente non azzera il muscolare, e viceversa un atleta con solo il
 * questionario ha comunque un fattore muscolare basato sul DOMS).
 *
 * `undefined` se non c'è nessuno dei due — il chiamante lo tratta come fattore
 * non misurato (escluso dal RI, non zero), esattamente come gli altri fattori.
 */
function muscularRecovery(cmjScore, domsScore) {
    var _a, _b;
    const factors = {};
    if (cmjScore != null)
        factors.cmj = cmjScore;
    if (domsScore != null)
        factors.doms = domsScore;
    const available = new Set(Object.keys(factors));
    if (available.size === 0)
        return undefined;
    const adjusted = rebalanceWeights(exports.MUSCULAR_WEIGHTS, available);
    let score = 0;
    for (const key of available) {
        score += ((_a = factors[key]) !== null && _a !== void 0 ? _a : 0) * ((_b = adjusted[key]) !== null && _b !== void 0 ? _b : 0);
    }
    return Math.round(score * 10) / 10;
}
